import { useEffect, useRef } from "react";

export default function AboutSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("opacity-100", "translate-y-0");
          el.classList.remove("opacity-0", "translate-y-6");
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-14 md:py-20 lg:py-24 px-4 md:px-6 lg:px-10 bg-background-50">
      <div
        ref={ref}
        className="max-w-7xl mx-auto opacity-0 translate-y-6 transition-all duration-700 ease-out"
      >
        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
          <div className="lg:w-[40%] flex-shrink-0">
            <span className="inline-block text-xs font-medium text-foreground-500 tracking-widest uppercase border border-foreground-200/60 px-3 py-1 rounded-full mb-5">
              Why GOOD RIDES
            </span>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground-950 leading-tight mb-4">
              Comfort &amp; Trust
              <br />
              <span className="italic text-foreground-500 font-normal">
                In Perfect Harmony
              </span>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5">
              Don&rsquo;t waste time in LA&rsquo;s complex traffic. Ece Czerw is a
              professional driver with 10+ years of experience who knows every
              corner of the city. With a luxury fleet, 24/7 service, and a
              personalized approach, every journey becomes an exceptional
              experience.
            </p>
            <a
              href="#reservation"
              className="inline-flex items-center gap-2 text-sm font-medium text-foreground-950 underline underline-offset-4 decoration-primary-400 hover:text-primary-600 transition-colors"
            >
              <span>Book Your Ride</span>
              <i className="ri-arrow-right-line" />
            </a>
          </div>

          <div className="lg:w-[55%] relative">
            <div className="flex justify-end gap-4 mb-6 lg:mb-0 lg:absolute lg:-top-4 lg:right-0 z-10 lg:bg-background-50/80 lg:backdrop-blur-sm lg:px-4 lg:py-3 lg:rounded-xl">
              <div className="text-center">
                <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  10+
                </div>
                <div className="text-xs text-foreground-500 mt-1">
                  Years Experience
                </div>
              </div>
              <div className="text-center">
                <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  500+
                </div>
                <div className="text-xs text-foreground-500 mt-1">
                  Happy Clients
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <div className="relative rounded-2xl overflow-hidden aspect-square">
                <img
                  src="/photos/driver-portrait.webp"
                  alt="Ece Czerw, professional private driver in Los Angeles, beside her black vehicle at sunset"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="relative rounded-2xl overflow-hidden aspect-[4/5] mt-8">
                <img
                  src="/photos/fleet-showcase.webp"
                  alt="Black Cadillac Escalade fleet interior and exterior for GOOD RIDES LA private chauffeur service"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}